// COMPONENT: CreateChildSupportOrderButton.tsx
import React, { useState } from "react";
import { Button } from "../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../design/dialog";
import { Input } from "../design/input";
import { Field, Fieldset, FieldGroup, Label } from "../design/fieldset";
import { useChildSupportOrder } from "../contexts/ChildSupportOrderContext";
import { PlusIcon } from "@heroicons/react/20/solid";
import { Select } from "../design/select";
import { Participant } from "../models/Participant";
import SelectParticipantField from "./Fields/SelectParticipantField";

const CreateChildSupportOrderButton: React.FC<{
  className?: string;
  children?: React.ReactNode;
}> = ({ className, children }) => {
  const { createChildSupportOrder } = useChildSupportOrder();
  const [isOpen, setIsOpen] = useState(false);
  const [amount, setMonthlyAmount] = useState<number>(850);
  const [startDate, setStartDate] = useState<string>(
    new Date().toISOString().split("T")[0],
  );
  const [endDate, setEndDate] = useState<string>("");
  const [status, setStatus] = useState("ordered");
  const [payer, setPayer] = useState<Participant["_id"] | null>(null);
  const [receiver, setReceiver] = useState<Participant["_id"] | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const clearForm = () => {
    setMonthlyAmount(850);
    setStartDate(new Date().toISOString().split("T")[0]);
    setEndDate("");
    setStatus("ordered");
    setPayer(null);
    setReceiver(null);
    setIsOpen(false);
  };

  const handleSubmit = async () => {
    if (!payer || !receiver) {
      alert("Please select both payer and receiver.");
      return;
    }
    setIsSubmitting(true);
    try {
      await createChildSupportOrder({
        amount,
        frequency: "monthly",
        startDate: new Date(startDate),
        endDate: endDate ? new Date(endDate) : undefined,
        status: status as any,
        payer,
        receiver,
      });
      clearForm();
    } catch (err) {
      console.error("Failed to create order", err);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button
        className={className}
        type="button"
        onClick={() => setIsOpen(true)}
      >
        {children ?? (
          <>
            <PlusIcon className="w-4 h-4" />
            <span className="hidden sm:block">Create Support Order</span>
          </>
        )}
      </Button>
      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>Create Child Support Order</DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Monthly Amount</Label>
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setMonthlyAmount(Number(e.target.value))}
                />
              </Field>
            </FieldGroup>
            <FieldGroup>
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
                <Field>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </Field>
                <Field>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </Field>
              </div>
            </FieldGroup>
            <FieldGroup>
              <Field>
                <Label>Status</Label>
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="pending">Pending</option>
                  <option value="ordered">Ordered</option>
                  <option value="modified">Modified</option>
                  <option value="terminated">Terminated</option>
                </Select>
              </Field>
              <SelectParticipantField
                label="Payer"
                value={payer}
                onChange={setPayer}
              />
              <SelectParticipantField
                label="Receiver"
                value={receiver}
                onChange={setReceiver}
              />
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={clearForm}>
            Cancel
          </Button>
          <Button disabled={isSubmitting} onClick={handleSubmit}>
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateChildSupportOrderButton;
